<template>
  <v-container fluid class="modal-content">
    <v-row>
      <v-col cols="12" md="8" lg="9" class="inv-ctn">
        <!-- header -->
        <div class="d-flex mb-3">
          <h4 class="align-self-center d-none d-sm-block font-weight-bold text-h6 lh-1 mr-2">
            Item shop
          </h4>

          <v-spacer />

          <div class="bg-ui-panel--dark text-body-2 px-3 d-flex align-center text-center mr-2">
            <scrap :amount="user.balance" />
          </div>

          <v-text-field
            :value="inputs.search"
            class="mr-2 rounded-0"
            background-color="grey800"
            hide-details solo dense flat
            :placeholder="$t('common.search')"
            @input="onSearchInput"
          />

          <v-menu
            bottom left offset-y
            :close-on-content-click="false"
            nudge-bottom="4"
            content-class="rounded-0"
            max-width="280"
          >
            <template #activator="{ on: menu }">
              <v-tooltip top color="grey800">
                <template #activator="{ on: tooltip }">
                  <v-btn
                    color="grey300 grey100--text"
                    class="px-3 unwidth unheight mr-2"
                    :ripple="false" tile v-on="{...menu, ...tooltip}"
                  >
                    <fai class="text-body-1" fixed-width :icon="['fas','filter']" />
                  </v-btn>
                </template>
                <span v-text="'Filters'" />
              </v-tooltip>
            </template>
            <v-card color="grey900" elevation="20" tile class="px-6 py-4">
              <v-row>
                <v-col cols="12">
                  <v-switch
                    v-model="inputs.inStock"
                    class="mt-0" dense
                    hide-details
                    inset color="green500"
                    label="Only in-stock"
                  />
                </v-col>
                <v-col cols="12">
                  <div class="price-slider grey600 py-1 px-2 mb-2">
                    <v-range-slider
                      color="primary500"
                      class="flex-shrink-1"
                      hide-details
                      :max="highestItemValue + 100"
                      :value="inputs.price"
                      thumb-label
                      :thumb-size="30"
                      @input="onPriceRangeChange"
                    >
                      <template #thumb-label="{ value }">
                        <div class="primary500 px-3 py-2 text-body-2 font-weight-bold">
                          {{ value | toScrap }}
                        </div>
                      </template>
                    </v-range-slider>
                  </div>

                  <v-row dense>
                    <v-col cols="6" class="d-flex">
                      <v-currency-field
                        :value="inputs.price[0]"
                        :options="{ allowNegative: false, valueRange: { max: highestItemValue }}"
                        placeholder="Min"
                        class="slider-input flex-grow-0 rounded-0 grey100--text text-caption"
                        solo hide-details dense
                        flat background-color="grey500"
                        @input="onPriceRangeChange([$event, inputs.price[1]])"
                      />
                    </v-col>
                    <v-col cols="6" class="d-flex justify-end">
                      <v-currency-field
                        :value="inputs.price[1]"
                        :options="{ allowNegative: false, valueRange: { min: .01, max: highestItemValue + 100 }}"
                        placeholder="Max"
                        class="slider-input flex-grow-0 rounded-0 grey100--text text-caption"
                        solo hide-details dense
                        flat background-color="grey500"
                        @input="onPriceRangeChange([inputs.price[0], $event])"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-menu>


          <v-tooltip top color="grey800">
            <template #activator="{on}">
              <v-btn
                color="grey300 grey100--text"
                class="px-2 unwidth unheight mr-2"
                :ripple="false" tile v-on="on"
                @click="inputs.sortAsc = !inputs.sortAsc"
              >
                <fai
                  class="text-h6" fixed-width
                  :icon="['fad', inputs.sortAsc ? 'sort-numeric-down' : 'sort-numeric-up']"
                />
              </v-btn>
            </template>
            <span v-text="'Sorting'" />
          </v-tooltip>

          <v-tooltip top color="grey800">
            <template #activator="{on}">
              <v-btn
                color="blue700" class="px-3 unwidth unheight"
                :ripple="false" :disabled="loading.inventory" tile
                @click="getInventory" v-on="on"
              >
                <fai class="blue500--text text-body-1" :spin="loading.inventory" fixed-width :icon="['fas','sync']" />
              </v-btn>
            </template>
            <span v-text="'Refresh'" />
          </v-tooltip>
        </div>

        <!-- Items -->
        <div>
          <Inventory
            v-if="filteredItems.length || loading.inventory"
            :items="filteredItems"
            :loading="loading.inventory"
            :item-gap="1"
            :item-component="itemComponent"
            @onItemClick="selectItem"
            @onItemAmountChange="changeItemAmount"
          />
          <div v-else class="faded-5 d-flex align-center justify-center grey300 py-16 mb-3">
            <span v-if="inputs.search" v-t="'inventory.no_items_filtered'" />
            <span v-else v-t="'inventory.no_items'" />
          </div>
        </div>
      </v-col>

      <!-- cart ctn -->
      <v-col cols="12" md="4" lg="3" class="sidebar d-flex flex-column">
        <div class="flex-grow-0 bg-ui-panel--dark d-flex align-center text-uppercase py-3 px-4">
          <span v-t="'page.shop.cart.selections'" class="text-body-2 text--secondary font-weight-semibold" />
          <v-spacer />
          <span v-t="'page.shop.cart.cost'" class="text-body-2 text--secondary font-weight-semibold" />
        </div>

        <div class="cart-items-container flex-grow-1">
          <div class="cart-items pt-2 d-flex flex-column">
            <!-- <CartItem
              v-for="item in selected" :key="item.id"
              :item="item"
              @remove="onItemRemove"
            /> -->

            <div
              v-for="item in selected" :key="item.id"
              class="cart-item bg-ui-panel d-flex align-center mb-1 pa-2"
            >
              <div class="item-info flex-grow-1 d-flex align-center">
                <div class="bg-ui-panel flex-shrink-0 remove-container pa-1 flex-grow-0 mr-3">
                  <v-img
                    :src="`https://community.cloudflare.steamstatic.com/economy/image/${item.image}/164x164`"
                    aspect-ratio="1" width="40"
                  />
                  <a class="pa-4 d-flex align-center justify-center flex-column remove-btn" @click="removeItem(item)">
                    <fai class="primary100--text text-h5" :icon="['far', 'times-circle']" />
                  </a>
                </div>
                <div class="grey100--text">
                  <div class="text-body-2 lh-1-3">
                    <span v-if="item.amountSelected > 1" class="font-weight-semibold blue300--text">
                      {{ item.amountSelected }}x
                    </span>
                    {{ item.name }}
                  </div>

                  <!-- stock warning -->
                  <div
                    v-if="inputs.inStock && item.amountSelected > item.inStock"
                    class="lh-1-3 text-caption primary400--text font-weight-bold"
                  >
                    ({{ (item.inStock || 0) | toLocale }} in stock)
                  </div>
                </div>
              </div>
              <div class="px-2">
                <scrap class="text-body-2" :amount="item.price * item.amountSelected" />
              </div>
            </div>

            <div class="bg-ui-panel--light flex-grow-1 flex-shrink-1" />
          </div>
        </div>

        <div class="flex-grow-0 bg-ui-panel--light d-flex align-center py-3 px-4 mb-2">
          <span class="text-body-2 text-uppercase text--secondary font-weight-semibold">
            {{ selectedCount }} / {{ maxItemSelect }}
            <span v-text="$tc('common.items', 2)" />
          </span>
          <v-spacer />
          <scrap class="text-body-2" :amount="selectedValue" />
        </div>


        <div
          v-if="showStockWarning"
          class="flex-grow-0 primary800 primary300--text pa-3 text-caption lh-1 font-weight-semibold mb-2 d-flex align-center"
        >
          <fai :icon="['fad', 'exclamation-triangle']" class="primary400--text text-h6 mr-3" />
          <div>
            <h4 class="mb-1">
              Some items are NOT in stock!
            </h4>
            <span class="primary100--text">Check warnings on items in cart.</span>
          </div>
        </div>

        <div
          v-if="betRequirement > 0"
          class="flex-grow-0 primary800 pa-3 d-flex align-center"
        >
          <fai :icon="['fad', 'exclamation-triangle']" class="primary400--text text-h6 mr-3" />
          <div class="lh-1">
            <h4 class="text-body-2 font-weight-bold lh-1-3 primary200--text">
              Bet requirement
            </h4>
            <i18n class="text-caption lh-1 primary100--text" path="page.shop.problem.bet">
              <scrap :amount="betRequirement" />
            </i18n>
          </div>
        </div>


        <v-btn
          v-else
          color="green800 green500--text"
          large depressed :ripple="false" block
          class="rounded-0 px-8 font-weight-bold flex-grow-0"
          :loading="loading.buy"
          :disabled="!canBuy"
          @click="onBuy"
        >
          <span>Buy</span>
          <span v-if="selectedCount" class="ml-1">
            {{ selectedCount }} {{ $tc('common.items', selectedCount) }}
          </span>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import sortBy from 'lodash.sortby'
import { debounce } from '@/utils'

import { itemPickupSound } from '@/modules/item-sounds'
import Inventory from '@/components/Inventory'
import ShopItem from '@/components/ShopItem'

export default {
  components: {
    Inventory,
  },
  props: {
    close: { type: [Function, Object], required: true },
    payload: { type: Object, required: true },
  },
  data() {
    return {
      loading: {
        inventory: false,
        buy: false,
      },
      inputs: {
        search: '',
        price: [0, 0],
        sortAsc: false,
        inStock: false,
      },
      items: [],
    }
  },
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
      betRequirement: 'user/betRequirement',
      settings: 'settings/settings',
      itemdb: 'items/items',
    }),
    itemComponent() {
      return ShopItem
    },
    config() {
      return this.conga.backpack.buy
    },
    maxItemSelect() {
      return this.config.maxSelect ?? 50
    },
    minValue() {
      return this.config.minValue ?? 1
    },
    //
    invValue() {
      return this.items.reduce((acc, i) => acc + i.price * i.amount, 0)
    },
    selected() {
      return this.items.filter(i => i.selected)
    },
    selectedCount() {
      return this.selected.reduce((acc, i) => acc + (i.amountSelected || 1), 0)
    },
    selectedValue() {
      return this.selected.reduce((acc, i) => acc + (i.price * (i.amountSelected || 1)), 0)
    },
    //
    highestItemValue() {
      return this.items[0]?.price ?? 0
    },
    canBuy() {
      if (this.selectedValue < this.minValue) return false
      if (this.betRequirement > 0) return false
      if (this.selectedCount > this.maxItemSelect) return false
      if (this.selectedValue > this.user.balance) return false

      return true
    },
    filteredItems() {
      let items = [...this.items]

      const {
        search, price, sortAsc, inStock,
      } = this.inputs

      if (search) {
        const searchText = search.trim().toLowerCase()
        items = items.filter(i => i.name.toLowerCase().includes(searchText))
      }

      const [min, max] = price
      items = items.filter(i => i.price <= max && i.price >= min)

      if (sortAsc) {
        items.reverse()
      }

      if (inStock) {
        items = items.filter(i => i.inStock)
      }

      return items
    },
    //
    showStockWarning() {
      return this.canBuy && this.inputs.inStock && this.selected.some(i => i.amountSelected > i.inStock)
    },
  },
  created() {
    if (this.payload.inStock) {
      this.inputs.inStock = true
    }

    this.getInventory()
    this.getUserInfo().catch(console.error)
  },
  methods: {
    ...mapActions({
      getUserInfo: 'user/getInfo',
    }),
    async getInventory() {
      if (this.loading.inventory) return
      this.loading.inventory = true

      const { itemdb } = this

      try {
        const items = await this.$socket.request('store.listings')

        const sortedItems = sortBy(
          items
            .map(item => {
              if (!itemdb[item.name]) return false

              return {
                ...itemdb[item.name],
                id: item.name, // only one of each item in this endpoint
                name: item.name,
                price: item.price,
                amount: 100,
                canBet: true,
                canWithdraw: true,
                selected: false,
                amountSelected: 1,
                inStock: item.inStock,
              }
            })
            .filter(Boolean),
          'price',
        ).reverse()

        // set this before we set this.items to avoid double render
        this.inputs.price = [0, (sortedItems[0]?.price ?? 0) + 100]

        this.items = sortedItems
      } catch (err) {
        this.$toast.error(err.message)
      } finally {
        this.loading.inventory = false
      }
    },
    async onBuy() {
      if (this.loading.buy) return

      const selected = this.selected.map(i => ({
        name: i.name,
        price: i.price,
        count: i.amountSelected,
      }))

      if (!selected.length) return

      try {
        this.loading.buy = true
        await this.$socket.request('store.purchase', selected)

        this.$toast.success('Your items have been added to your backpack.')
        this.close()
      } catch (err) {
        this.$toast.error(err.message)
      } finally {
        this.loading.buy = false
      }
    },
    selectItem(item) {
      if (this.loading.buy) return
      if (!item.selected && this.selectedCount >= this.maxItemSelect) return

      const itemIndex = this.items.findIndex(i => i.id === item.id)
      if (itemIndex === -1) return

      this.items[itemIndex].selected = !item.selected

      if (!item.selected) {
        this.items[itemIndex].amountSelected = 1
      }

      this.playItemSound(item.type)
    },
    changeItemAmount(item, val) {
      if (this.loading.buy) return

      const itemIndex = this.items.findIndex(i => i.id === item.id)
      if (itemIndex === -1) return

      const lItem = this.items[itemIndex]

      if (!lItem.selected) {
        lItem.selected = true
        lItem.amountSelected = 1
        this.playItemSound(item.type)
        return
      }

      // wants to remove
      if (val < 1) {
        lItem.selected = false
        lItem.amountSelected = 1
        this.playItemSound(item.type)
        return
      }

      if (item.amount < 2) return
      if (val > item.amount) return

      lItem.selected = true
      lItem.amountSelected = val

      this.playItemSound(item.type)
    },
    removeItem(item) {
      if (this.loading.buy) return

      const itemIndex = this.items.findIndex(i => i.id === item.id)
      if (itemIndex === -1) return

      this.items[itemIndex].selected = false
      this.items[itemIndex].amountSelected = 1

      this.playItemSound(item.type)
    },
    playItemSound(type) {
      const volume = this.settings.sounds.volumes.items
      itemPickupSound(type, volume)
    },
    onPriceRangeChange: debounce(function onChange(range) {
      this.inputs.price = range
    }, 200),
    onSearchInput: debounce(function onChange(search) {
      this.inputs.search = search
    }, 200),
  },
}
</script>

<style lang="scss" scoped>
.inventory {
  min-height: 400px;
  max-height: 600px;
}

.sidebar {
  .cart-items-container {
    position: relative;
    height: auto;
    min-height: 200px;

    .cart-items {
      overflow-y: auto;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      @include no-scrollbar;

      .remove-container {
        overflow: hidden;
        position: relative;

        .remove-btn {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          background: rgba(primary(500), 0.75);
          transition: opacity 0.3s ease, transform 0.3s ease;
          transform: scale(0.7);
        }
      }

      .cart-item {
        &:hover {
          .remove-btn {
            opacity: 1;
            transform: scale(1);
          }
        }
      }
    }
  }
}

.price-slider {
  .v-input__slider ::v-deep {
    .v-slider__track-container,
    .v-slider__ticks-container {
      height: 6px !important;
    }

    .v-slider__tick {
      top: calc(50% - 2px) !important;
      height: 4px !important;
    }

    .v-slider__thumb {
      width: 10px;
      height: 24px;
      border: 2px solid #cecece !important;
      border-radius: 0;
      cursor: grab;

      &::before {
        display: none;
      }
    }
  }
}

.slider-input {
  width: 80px;
}

.bg-ui-panel {
  background-color: rgba(#6e6b65, 0.75);
}

::v-deep .inv-bg {
  background-color: rgba(lighten(#6e6b65, 4%), 0.75);
}

.bg-ui-panel--light {
  background-color: rgba(#6e6b65, 0.45);
}

.bg-ui-panel--dark {
  background-color: rgba(#6e6b65, 0.85);
}
</style>
